/**
 * @voltxampere/engine — Assertion DSL lexer (Sprint 9)
 *
 * Converts a DSL source string into a stream of {@link Token}s
 * per {@link ./tokens.ts}. The lexer is locale-aware: Turkish and
 * English keyword sets are selected at construction and the
 * resulting tokens always carry the canonical (locale-free)
 * keyword name, so the parser never branches on language.
 *
 * Intentionally permissive on whitespace, strict on syntax:
 *
 *   - unknown characters → {@link AssertionLexError} with the
 *     exact column so i18n messages can point at the offending
 *     char,
 *   - unterminated number literals (`1.e`) → error with the
 *     partial slice,
 *   - stray `%`, `±`, `=` → tokenised normally; the parser
 *     decides whether they belong.
 *
 * The lexer canonicalises four forms:
 *
 *   - `~=` → `≈`, `<=` → `≤`, `>=` → `≥`, `+/-` / `+-` → `±`,
 *   - `Ohm` → `Ω`,
 *   - `u` (as SI prefix only) → `μ`.
 *
 * A unit token is only produced when an identifier-like run
 * immediately follows a `NUMBER` AND the run classifies against
 * the SPECS §6 unit dictionary. This keeps a net named `mA` from
 * being mistaken for a milliamp literal when it stands alone.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { BASE_UNITS, COMPARATOR_ASCII_MAP, SI_PREFIXES, isIdentifierContinue, isIdentifierStart, keywordsFor, } from "./tokens.js";
/** Error thrown by the lexer on unrecognised syntax. */
export class AssertionLexError extends Error {
    column;
    char;
    constructor(message, column, char) {
        super(`${message} (column ${String(column)})`);
        this.name = "AssertionLexError";
        this.column = column;
        this.char = char;
    }
}
export class AssertionLexer {
    source = "";
    position = 0;
    locale = "en";
    lastKind = null;
    tokenize(input, options = {}) {
        this.source = input;
        this.position = 0;
        this.locale = options.locale ?? "en";
        this.lastKind = null;
        const out = [];
        while (this.position < this.source.length) {
            this.skipWhitespace();
            if (this.position >= this.source.length)
                break;
            const tok = this.readToken();
            out.push(tok);
            this.lastKind = tok.kind;
        }
        out.push({ kind: "EOF", value: "", position: this.source.length, length: 0 });
        return out;
    }
    skipWhitespace() {
        while (this.position < this.source.length) {
            const ch = this.source[this.position];
            if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
                this.position += 1;
            }
            else {
                break;
            }
        }
    }
    readToken() {
        const start = this.position;
        const ch = this.source[start];
        if (ch === undefined) {
            throw new AssertionLexError("Unexpected end of input", start, "");
        }
        // 1. Locale-aware keywords before generic identifiers
        const kw = this.matchKeyword(start);
        if (kw !== null)
            return kw;
        // 2. Multi-character operators / tolerance
        const twoChar = this.source.slice(start, start + 2);
        if (COMPARATOR_ASCII_MAP.has(twoChar)) {
            const canon = COMPARATOR_ASCII_MAP.get(twoChar);
            if (canon !== undefined) {
                this.position = start + 2;
                return { kind: "COMPARATOR", value: canon, position: start, length: 2 };
            }
        }
        if (twoChar === "+/" && this.source[start + 2] === "-") {
            this.position = start + 3;
            return { kind: "TOLERANCE", value: "±", position: start, length: 3 };
        }
        if (twoChar === "+-") {
            this.position = start + 2;
            return { kind: "TOLERANCE", value: "±", position: start, length: 2 };
        }
        // 3. Single-character tokens
        switch (ch) {
            case "(":
                this.position = start + 1;
                return { kind: "LPAREN", value: "(", position: start, length: 1 };
            case ")":
                this.position = start + 1;
                return { kind: "RPAREN", value: ")", position: start, length: 1 };
            case ",":
                this.position = start + 1;
                return { kind: "COMMA", value: ",", position: start, length: 1 };
            case "=":
                this.position = start + 1;
                return { kind: "EQUALS", value: "=", position: start, length: 1 };
            case "%":
                this.position = start + 1;
                return { kind: "PERCENT", value: "%", position: start, length: 1 };
            case "±":
                this.position = start + 1;
                return { kind: "TOLERANCE", value: "±", position: start, length: 1 };
            case "≈":
            case "≤":
            case "≥":
            case "<":
            case ">":
                this.position = start + 1;
                return { kind: "COMPARATOR", value: ch, position: start, length: 1 };
            case "+":
            case "-":
            case "*":
            case "/":
                this.position = start + 1;
                return { kind: "OPERATOR", value: ch, position: start, length: 1 };
            default:
                break;
        }
        // 4. Number (leading digit OR leading `.` followed by digit)
        if ((ch >= "0" && ch <= "9") || (ch === "." && this.isDigitAt(start + 1))) {
            return this.readNumber(start);
        }
        // 5. Identifier (which may be a UNIT when following a NUMBER)
        if (isIdentifierStart(ch)) {
            return this.readIdentifierOrUnit(start);
        }
        throw new AssertionLexError(`Unexpected character '${ch}'`, start, ch);
    }
    isDigitAt(pos) {
        const ch = this.source[pos];
        return ch !== undefined && ch >= "0" && ch <= "9";
    }
    matchKeyword(start) {
        const kws = keywordsFor(this.locale);
        const buckets = [
            { canonical: "allTimes", phrases: kws.allTimes },
            { canonical: "conditionWhen", phrases: kws.conditionWhen },
            { canonical: "conditionAt", phrases: kws.conditionAt },
            { canonical: "assertion", phrases: kws.assertion },
            { canonical: "simEqualsAlias", phrases: kws.simEqualsAlias },
            { canonical: "oscillates", phrases: kws.oscillates },
            { canonical: "source", phrases: kws.source },
        ];
        // Longest phrase wins — so multi-word `ne zaman` beats
        // single-word `zaman`, and `all times` beats `at`.
        const sorted = buckets.flatMap((b) => b.phrases.map((p) => ({ canonical: b.canonical, phrase: p })));
        sorted.sort((a, b) => b.phrase.length - a.phrase.length);
        for (const entry of sorted) {
            const phrase = entry.phrase;
            const slice = this.source.slice(start, start + phrase.length);
            if (slice.toLowerCase() !== phrase.toLowerCase())
                continue;
            // For multi-word keywords the internal whitespace is part
            // of the match. For both, we demand a boundary after the
            // keyword so `assert` doesn't swallow `assertion` etc.
            const next = this.source[start + phrase.length];
            if (next !== undefined && isIdentifierContinue(next))
                continue;
            this.position = start + phrase.length;
            return {
                kind: "KEYWORD",
                value: entry.canonical,
                position: start,
                length: phrase.length,
            };
        }
        return null;
    }
    readNumber(start) {
        let i = start;
        while (i < this.source.length && this.isDigitAt(i))
            i += 1;
        if (this.source[i] === ".") {
            i += 1;
            while (i < this.source.length && this.isDigitAt(i))
                i += 1;
        }
        if (this.source[i] === "e" || this.source[i] === "E") {
            i += 1;
            if (this.source[i] === "+" || this.source[i] === "-")
                i += 1;
            const expDigitsStart = i;
            while (i < this.source.length && this.isDigitAt(i))
                i += 1;
            if (i === expDigitsStart) {
                throw new AssertionLexError("Incomplete exponent in number literal", start, this.source.slice(start, i));
            }
        }
        const slice = this.source.slice(start, i);
        this.position = i;
        if (slice === "." || slice === "") {
            throw new AssertionLexError("Invalid number literal", start, slice);
        }
        return { kind: "NUMBER", value: slice, position: start, length: i - start };
    }
    readIdentifierOrUnit(start) {
        let i = start;
        while (i < this.source.length) {
            const ch = this.source[i];
            if (ch === undefined || !isIdentifierContinue(ch))
                break;
            i += 1;
        }
        const slice = this.source.slice(start, i);
        this.position = i;
        const normalised = slice === "Ohm" ? "Ω" : slice;
        // Only classify as a unit if the previous token was a NUMBER.
        // Otherwise it's a bare identifier (net name, component id,
        // or function name).
        if (this.lastKind === "NUMBER") {
            const asUnit = this.classifyUnit(normalised);
            if (asUnit !== null) {
                return { kind: "UNIT", value: asUnit, position: start, length: i - start };
            }
        }
        return { kind: "IDENTIFIER", value: normalised, position: start, length: i - start };
    }
    classifyUnit(token) {
        if (BASE_UNITS.has(token)) {
            return token === "Ohm" ? "Ω" : token;
        }
        const prefixKeys = [...SI_PREFIXES.keys()].sort((a, b) => b.length - a.length);
        for (const pfx of prefixKeys) {
            if (token.startsWith(pfx) && token.length > pfx.length) {
                const rest = token.slice(pfx.length);
                if (BASE_UNITS.has(rest) || rest === "Ohm") {
                    const base = rest === "Ohm" ? "Ω" : rest;
                    const canonPfx = pfx === "u" ? "μ" : pfx;
                    return `${canonPfx}${base}`;
                }
            }
        }
        return null;
    }
}
/** Convenience one-shot entry. */
export function tokenize(input, options) {
    return new AssertionLexer().tokenize(input, options);
}
//# sourceMappingURL=lexer.js.map